<template>
  <b-card no-body class="segments-chart">
    <template slot="header">
      <div class="header">
        <div class="header__title">
          {{ title }}
        </div>

        <div class="header__extra">
          <slot name="header-extra">
            <!-- header right side extra content -->
          </slot>
        </div>
      </div>
    </template>
    <b-card-body>
      <div v-if="isEmpty" class="no-data">
        No data available
      </div>

      <div v-else class="chart-container">
        <slot name="top-controls-extra">
          <!-- for example <buttons-select /> -->
        </slot>

        <apexchart
          type="donut"
          height="98%"
          width="100%"
          :options="chartOptions"
          :series="chartSeries" />
      </div>

    </b-card-body>
  </b-card>
</template>

<script>
  import isEmpty from 'lodash-es/isEmpty';
  import getNested from 'lodash-es/get';
  import VueApexCharts from 'vue-apexcharts';
  import moment from 'moment';

  import GraphThemeMixin from '@/mixins/GraphThemeMixin';
  import { piePalette } from '@/shared/constants/colors';
  import { formatVolume } from '@/shared/number-utils';

  export default {
    name: 'segments-chart',

    components: {
      apexchart: VueApexCharts,
    },

    mixins: [
      GraphThemeMixin,
    ],

    props: {
      title: {
        type: String,
        default: 'No title provided',
      },

      showPercentage: {
        type: Boolean,
        default: false,
      },

      items: {
        type: Array,
        default: () => [],
      },
    },

    computed: {
      chartSeries() {
        return this.segmentVolumes;
      },

      chartOptions: function () {
        return {
          chart:{
            id: `graph${moment().format('YYYYMMDDHHmm')}`,
            background: this.themeBackground,  // from mixin
            toolbar: {
              show: true,
            },
          },

          labels: this.segmentLabels,
          // colors: ['#2E93fA'],
          colors: piePalette,
          tooltip: {
            enabled: false,
          },

          stroke: {
            show: false,
            // colors: ['#3a4149'],
            // width: 3,
          },

          theme: {
            // tooltip styling doesn't work correctly for now,
            // it's required to set the whole theme here
            mode: this.darkMode ? 'dark' : 'light',
          },

          legend: {
            show: true,
            position: 'bottom',
            height: 50,
            markers: {
                // hide markers
                width: 0,
                height: 0,
            },
            formatter: function(seriesName, opts) {
              const originalName = getNested(this.items, `${opts.seriesIndex}.segment`, 'Unknown segment');
              let value = formatVolume(opts.w.globals.series[opts.seriesIndex]);
              return `${originalName} (${value})`;
            }.bind(this),
          },

          dataLabels: {
            // labels for each sector
            enabled: true,
            formatter: function (val, opts) {
              return opts.w.config.labels[opts.seriesIndex];
            },
            style: {
              colors: [this.darkMode ? '#fff' : '#ececec'],
            },
          },

          plotOptions: {
            pie: {
              donut: {
                labels: {
                  // label at the center of the pie
                  show: true,
                  name: { show: true },
                  value: {
                    show: true,
                    formatter: function (value) {
                      return formatVolume(value);
                    },
                  },
                  total: { show: false },
                },
              },
            },
          },
        };
      },

      isEmpty() {
        return isEmpty(this.items);
      },

      segmentVolumes() {
        return this.items.map(item => item.volume);
      },

      segmentLabels() {
        let segmentNames = this.items.map(item => item.segment);

        if(this.showPercentage) {
          const total = this.segmentVolumes.reduce((a, b) => a + b, 0);

          return this.items.map(item => {
            let percent = 100 * (item.volume / total)
            percent = percent.toFixed(0);
            return `${item.segment} (${percent}%)`;
          });
        }

        return segmentNames;
      },
    },
  }
</script>

<style scoped>
  .segments-chart >>> .card-body {
    padding: 10px 0 0 0;
  }

  .segments-chart >>> .apexcharts-legend-series {
    text-align: center;
  }
</style>

<style scoped lang="scss">
  .segments-chart {
    width: 100%;

    .header {
      display: flex;
      justify-content: space-between;
    }

    .card-body {
      display: flex;
      justify-content: stretch;
      align-items: stretch;

      .no-data {
        width: 100%;
        padding-top: 10px;
        text-align: center;
      }

      .chart-container {
        width: calc(100% - 1px);
        //height: 400px;
      }
    }
  }
</style>
